import { BaseDialogComponent } from './../../../../../components/kit/base-dialog/base-dialog.component';
import { Component, inject, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { fb } from '../../../../shared/shared.global-service';
import { isInvalid } from '../../../../shared/helpers/validate';
import { PersonInsuranceDialogStore } from './person-insurance-dialog.store';
import { PersonInsuranceCreateInput } from '@libs/types/src/services/input-type/person/person-insurance.create-input';
import { PersonInsuranceUpdateInput } from '@libs/types/src/services/input-type/person/person-insurance.update-input';
import { PersonInsuranceObject } from '@libs/types/src/services/object-type/person/person-insurance.object';
import { dateify } from '../../../../shared/helpers/date.helper';
import { insuranceTypeEnumOptions } from '../../../../../../../../types/src/common/enum/insurance-type.enum';
import { isDateRangeFree } from '@libs/types/src/common/utils/date.utils';
import { getBilledWorksFrag, getInsurancesFrag } from './person-insurance-dialog.query';
import { contentGlobalSuccess } from '../../../content.error';
import { WorkGridItemObject } from '@it2go/types';
import { compareArrays } from '@libs/types/src/utils/array.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { ErrorFloatMessageComponent } from '../../../../../components/kit/float-message/error-float-message.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../../../components/medical-service/element/insurance-company-select-input/insurance-company-select-input.component';

export interface PersonInsuranceDialogData {
    insurance?: PersonInsuranceObject | null;
    personId?: number | null;
    insurances: PersonInsuranceObject[];
}

@Component({
    selector: 'app-person-insurance-dialog',
    templateUrl: './person-insurance-dialog.component.html',
    styleUrls: ['./person-insurance-dialog.component.scss'],
    providers: [PersonInsuranceDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        ErrorFloatMessageComponent,
        ReactiveFormsModule,
        SelectInputComponent,
        InsuranceCompanySelectInputComponent,
    ],
})
export class PersonInsuranceDialogComponent extends BaseDialogComponent implements OnInit {

    public readonly store = inject(PersonInsuranceDialogStore);

    public insurance: PersonInsuranceObject | null = null;
    public checkInsurances: PersonInsuranceObject[] = [];
    public personId: number | null = null;

    public created = output<any>();

    protected form = fb.group({
        insuredFrom: fb.control<Date | null>(null, [Validators.required]),
        insuredTo: fb.control<Date | null>(null, []),
        insuranceAbbr: fb.control<string | null>(null, [Validators.required]),
        insuranceType: fb.control<string | null>(null, [Validators.required]),
    });

    protected overlap = false;
    protected toBeforeFrom = false;
    protected workBilled = false;

    public ngOnInit(): void {
        this.insurance = this.dialogConfig.data?.insurance;
        this.personId = this.dialogConfig.data?.personId;
        if (this.insurance) {
            this.form.patchValue({
                ...this.insurance,
                insuredFrom: dateify(this.insurance.insuredFrom),
                insuredTo: dateify(this.insurance.insuredTo),
            });
        }
    }

    public override async save(): Promise<void> {
        this.toBeforeFrom = false;
        this.overlap = false;
        this.workBilled = false;

        if (this.form.value.insuredFrom && this.form.value.insuredTo) {
            this.toBeforeFrom = this.form.value.insuredTo <= this.form.value.insuredFrom;
        }

        if (this.personId) {
            const response = await this.store.api.getPersonInsurances(FilterBuilder.filter({
                id: { NEQ: this.insurance?.id || 0 },
                personId: this.personId,
                page: 1,
                limit: 200,
            }), getInsurancesFrag);
            this.checkInsurances = response['items'] ?? [];
            this.overlap = this.checkOverlap(this.checkInsurances);
        } else {
            this.checkInsurances = this.dialogConfig.data.insurances;
            this.overlap = this.checkOverlap(this.checkInsurances);
        }

        await this.checkBilledWorks();
        if (this.toBeforeFrom || this.overlap || this.workBilled) return;

        if (this.insurance) {
            await this.updateInsurance();
            this.personId && contentGlobalSuccess.next('common.updated');
        } else {
            await this.saveInsurance();
            this.personId && contentGlobalSuccess.next('common.created');
        }
    }

    private async updateInsurance(): Promise<void> {
        if (isInvalid(this.form)) return;

        const insurance: PersonInsuranceUpdateInput = {
            id: this.insurance!.id,
            insuredFrom: this.form.value.insuredFrom?.toISOString()!,
            insuredTo: this.form.value.insuredTo?.toISOString() || null,
            insuranceAbbr: this.form.value.insuranceAbbr!,
            insuranceType: this.form.value.insuranceType!,
        };

        this.personId && await this.store.api.updatePersonInsurance(insurance);
        this.dialogRef.close(insurance);
    }

    private async saveInsurance(): Promise<void> {
        if (isInvalid(this.form)) return;

        const insurance: PersonInsuranceCreateInput = {
            personId: this.personId || 0,
            insuredFrom: this.form.value.insuredFrom?.toISOString()!,
            insuredTo: this.form.value.insuredTo!?.toISOString(),
            insuranceAbbr: this.form.value.insuranceAbbr!,
            insuranceType: this.form.value.insuranceType!,
        };

        this.personId && await this.store.api.createPersonInsurance(insurance);
        this.dialogRef.close(insurance);
    }

    private checkOverlap(insurances: PersonInsuranceObject[]) {
        return !isDateRangeFree(
            {
                dateFrom: this.form.value.insuredFrom,
                dateTo: this.form.value.insuredTo,
            },
            insurances.filter(insurance => insurance.insuranceType === this.form.value.insuranceType)
                .map((it: PersonInsuranceObject) => ({
                    dateFrom: it.insuredFrom,
                    dateTo: it.insuredTo,
                })) || [],
            true,
        );
    }

    private async checkBilledWorks(): Promise<void> {
        if (!this.personId) return;

        const oldFrom: Date = dateify(this.insurance?.insuredFrom) || this.form.value.insuredFrom!;
        const oldTo: Date | null = dateify(this.insurance?.insuredTo);
        const newFrom: Date = this.form.value.insuredFrom!;
        const newTo: Date | null = this.form.value.insuredTo || null;

        if (this.insurance) {
            // Update -> check that insurance still matched same billed works
            const oldBilledInRange = await this.getBilledWorkIds(oldFrom, oldTo);
            const newBilledInRange = await this.getBilledWorkIds(newFrom, newTo);
            const diff = compareArrays(oldBilledInRange, newBilledInRange);
            this.workBilled = diff.aUnique.length > 0 || diff.bUnique.length > 0;

            // In case of altered insurance type, check no works were billed
            if (this.insurance.insuranceAbbr !== this.form.value.insuranceAbbr || this.insurance.insuranceType !== this.form.value.insuranceType) {
                const newBilledInRange = await this.getBilledWorkIds(oldFrom, oldTo);
                this.workBilled = newBilledInRange.length > 0;
            }
        } else {
            // Create -> check that new insurance does not overtake any billed work
            const newBilledInRange = await this.getBilledWorkIds(newFrom, newTo);
            this.workBilled = newBilledInRange.length > 0;
        }
    }

    private async getBilledWorkIds(dateFrom: Date, dateTo: Date | null = null): Promise<number[]> {
        const workFilter = FilterBuilder.where({
            personId: this.personId,
            isBilled: true,
            limit: 1,
        });
        workFilter.where('dateFrom', dateFrom.toISOString(), 'GTE');
        if (dateTo) {
            workFilter.orWhere('dateFrom', dateTo.toISOString(), 'LTE');
        }

        const works = await this.store.api.getWorks(workFilter.filter(), getBilledWorksFrag);

        return works?.items?.map((it: WorkGridItemObject) => it.id) || [];
    }

    protected readonly insuranceTypeEnumOptions = insuranceTypeEnumOptions;
}
